/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useEffect, useRef, useState, useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';

import FeedDropdownOptions from './FeedDropdownOptions';
import IsTypingComponent from './IsTypingComponent';
import PostAuthorAndDate from './PostAuthorAndDate';
import FeedPostComments from './FeedPostComments';
import NewCommentBox from './NewCommentBox';
import Text from '../../../../shared/_TBComponents/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

import useFeed from '../hooks/useFeed';
import {
  setScrollTrueFalse,
  openMessageVideo,
  openImageLightbox,
  setIsCommentClickedRedux,
  setActiveReduxModal,
  setReportPostInfo,
} from '../../ducks/feedActions';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import {
  transformPostText,
  imageFlipper,
  pictureSelector,
  findFeedIndex,
} from '../../helperFunctions';
import { feedTracker } from '../../../../shared/utils/amplitudeHelper';

const DropdownDiv = styled('div')``;

const FeedPostDiv = styled('div')`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 600px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0px 2px 4px 0px rgb(0 0 0 / 10%);
  @media screen and (max-width: 640px) {
    width: 100%;
  }
`;

const FistBumpContainer = styled('div')`
  font-family: 'Nunito Sans', sans-serif;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${(props) => (props.isBumped ? '#ff6600 !important' : '#9e9e9e !important')};
  transition: ${TransitionGlobal};
  svg {
    margin-right: 10px;
    path {
      transition: ${TransitionGlobal};
    }
  }
  :hover {
    color: ${(props) => (props.isBumped ? '#ff6600 !important' : '#444444 !important')};
    svg {
      path {
        fill: ${(props) => (props.isBumped ? '#ff6600 !important' : '#444444 !important')};
      }
    }
  }
  .fist-bump-counter {
    margin-right: 5px;
  }
`;

const InteriorDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  padding: 20px;
`;

const MainPostContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MediaBox = styled('div')`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 600px;
  width: 100%;
  position: relative;
  .media-image {
    max-height: 600px;
    width: 100%;
    object-fit: cover;
  }
`;

const PicAndHeader = styled('div')`
  display: flex;
`;

const PostActions = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: #777;
  font-weight: 400;
  font-size: 12px;
  user-select: none;
  margin-top: 10px;
  margin-left: 20px;
  gap: 18px;
`;

const PostCommentsContainer = styled('div')`
  font-family: 'Nunito Sans', sans-serif;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: ${TransitionGlobal};
  svg {
    margin-right: 10px;
    path {
      transition: ${TransitionGlobal};
    }
  }
  :hover {
    color: #444444;
    svg {
      path {
        fill: #444444 !important;
      }
    }
  }
  .post-comments-counter {
    font-family: 'Nunito Sans', sans-serif;
    margin-right: 5px;
  }
`;

const PostText = styled('div')`
  margin: 0 20px 10px 20px;
  white-space: pre-wrap;
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${(props) => (props.feedObject.media ? '16px' : '24px')};
`;

const ShowMoreButton = styled('div')`
  width: 120px;
  padding: 3px;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  :hover {
    background: rgba(3, 172, 240, 0.1);
  }
  cursor: pointer;
`;

const ShowMoreButtonWrapper = styled('div')`
  padding-left: 30px;
  margin-bottom: 10px;
`;

const UserPicDiv = styled('div')`
  height: 35px;
  width: 35px;
  border-radius: 50%;
  background-image: ${(feedObject) => `url(${pictureSelector(feedObject.props.userPic, 70, 60)})`};
  background-size: contain;
  min-width: 35px;
  margin-right: 15px;
`;

const VideoIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  width: 100px;
  border-radius: 50%;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.4);
  position: absolute;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  z-index: 1;
`;

const FeedPost = ({ feedObject: feedObjectProp }) => {
  const {
    handleSetEdit,
    handleDeleteFeedPost,
    handleFistBumpFeedPost,
  } = useFeed();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const feed = useSelector((state) => state.feed.data.feed);
  const pinnedFeed = useSelector((state) => state.feed.data.pinnedFeed);
  const scrollTrueId = useSelector((state) => state.feed.data.scrollTrueId);

  const feedObject = useMemo(() => {
    const allFeed = [...(feed || []), ...(pinnedFeed || [])];
    return allFeed.find((item) => item.id === feedObjectProp.id) || feedObjectProp;
  }, [feed, pinnedFeed, feedObjectProp]);

  const [bumpCounter, setBumpCounter] = useState(feedObject.fistBumps);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [isBumped, setIsBumped] = useState(feedObject.fistBumpActive);

  const dispatch = useDispatch();

  const feedPostRef = useRef();

  useEffect(() => {
    setBumpCounter(feedObject.fistBumps);
    setIsBumped(feedObject.fistBumpActive);
  }, [feedObject.fistBumps, feedObject.fistBumpActive]);

  useEffect(() => {
    if (feedPostRef.current) {
      if (scrollTrueId === feedObject.id) {
        feedPostRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
        setScrollTrueFalse();
      }
    }
  }, [scrollTrueId]);

  const handleEdit = () => {
    handleSetEdit(feedObject);
    feedTracker('Normal Mode', 'Edit post modal opened');
  };

  const handleDeletePost = () => {
    const feedObjectIndex = findFeedIndex(feedObject, feed, pinnedFeed);
    const { pinned } = feedObject;

    handleDeleteFeedPost({
      accountCode: currentUser.accountCode,
      feedObjectIndex,
      feedObjectId: feedObject.id,
      pinned,
    });
    feedTracker('Normal Mode', 'Post deleted');
  };

  const handleReportPost = () => {
    dispatch(setActiveReduxModal('report-post-modal'));
    dispatch(setReportPostInfo('Post', feedObject.id));
    feedTracker('Normal Mode', 'Report modal opened - post');
  };

  const dropdownOptions = () => (
    <FeedDropdownOptions
      contentObject={feedObject}
      editFunction={handleEdit}
      editText='Edit'
      deleteFunction={handleDeletePost}
      reportFunction={handleReportPost}
    />
  );

  const handleVideoClick = () => {
    dispatch(openMessageVideo(feedObject.media.mediaUrl));
    feedTracker('Normal Mode', 'Post video clicked');
  };

  const fistBumpFeedPostOnSuccess = (updatedBumpState, updatedBumpCounter) => {
    setIsBumped(updatedBumpState);
    setBumpCounter(updatedBumpCounter);
  };

  const handleClickFistBump = () => {
    const feedObjectIndex = findFeedIndex(feedObject, feed, pinnedFeed);
    const { pinned } = feedObject;
    const newBumpState = !feedObject.fistBumpActive;
    handleFistBumpFeedPost({
      accountCode: currentUser.accountCode,
      bool: newBumpState,
      feedPostId: feedObject.id,
      feedObjectIndex,
      pinned,
      onSuccess: fistBumpFeedPostOnSuccess,
    });
    feedTracker('Normal Mode', `Fist bump feed post ${newBumpState ? 'activated' : 'deactivated'}`);
  };

  const handleClickPostComments = () => {
    if (feedObject.replyAllowed) {
      const newOb = {};
      newOb[feedObject.id] = true;
      dispatch(setIsCommentClickedRedux(newOb));
    }
  };

  const handleMediaClick = () => {
    if (
      feedObject.media.type === 'IMAGE'
      || feedObject.media.type === 'EXTERNAL_URL'
    ) {
      dispatch(openImageLightbox(feedObject.media.mediaUrl));
      feedTracker('Normal Mode', 'Post image/GIF clicked');
    }
  };

  const handleShowMoreClick = () => {
    setCommentsExpanded((prevExpandedState) => {
      const newExpandedState = !prevExpandedState;

      // If we're collapsing comments (setting to false), scroll back to feedPostRef
      if (!newExpandedState && feedPostRef.current) {
        feedPostRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      return newExpandedState;
    });
    feedTracker('Normal Mode', commentsExpanded ? 'Show less comments clicked' : 'Show more comments clicked');
  };

  const renderMediaImage = () => feedObject.media.mediaUrl && (
  <img
    src={imageFlipper(feedObject.media)}
    alt='media'
    className='media-image'
  />
  );

  const renderVideoIcon = () => feedObject.media.type === 'VIDEO' && (
  <VideoIcon onClick={handleVideoClick} aria-label='Play video'>
    <IcomoonReact iconSet={iconSet} size={50} icon='altrightarrow' />
  </VideoIcon>
  );

  const renderMedia = () => {
    if (!feedObject.media || !feedObject.media.mediaUrl) return null;

    return (
      <MediaBox onClick={handleMediaClick} aria-label='View media'>
        {renderMediaImage()}
        {renderVideoIcon()}
      </MediaBox>
    );
  };

  const renderCommentsSection = () => {
    if (!feedObject?.replies?.length) return null;

    return (
      <>
        <FeedPostComments
          feedObject={feedObject}
          isCollapsed={!commentsExpanded}
          setIsCollapsed={setCommentsExpanded}
        />
        {feedObject.replies.length > 2 && (
          <ShowMoreButtonWrapper>
            <ShowMoreButton onClick={handleShowMoreClick} aria-label={commentsExpanded ? 'Show less comments' : 'Show more comments'}>
              <Text
                styledTextProps={{
                  fontSize: 12,
                  fontWeight: 900,
                  letterSpacing: 1.3,
                }}
              >
                {commentsExpanded ? 'SHOW LESS' : 'SHOW MORE'}
              </Text>
            </ShowMoreButton>
          </ShowMoreButtonWrapper>
        )}
      </>
    );
  };

  const renderPostActionsSection = () => (
    <PostActions isBumped={isBumped}>
      <FistBumpContainer onClick={handleClickFistBump} isBumped={isBumped} aria-label={`${isBumped ? 'Remove fist bump' : 'Add fist bump'} - ${bumpCounter} ${bumpCounter === 1 ? 'fist bump' : 'fist bumps'}`}>
        <IcomoonReact
          iconSet={iconSet}
          size={50}
          icon={isBumped ? 'fist-bump-full-together' : 'fist-bump-full-apart'}
          color={isBumped ? '#ff6600' : '#9e9e9e'}
        />
        <div className='fist-bump-counter'>{bumpCounter}</div>
        <div>{bumpCounter === 1 ? 'Fist Bump' : 'Fist Bumps'}</div>
      </FistBumpContainer>
      <PostCommentsContainer onClick={handleClickPostComments} aria-label={feedObject.replyAllowed ? `View comments - ${feedObject.replies.length} ${feedObject.replies.length === 1 ? 'comment' : 'comments'}` : 'Comments disabled'}>
        <IcomoonReact
          iconSet={iconSet}
          size={22}
          icon={feedObject.replyAllowed ? 'comment' : 'comment-disabled'}
          color='#9e9e9e'
        />
        {feedObject.replyAllowed ? (
          <>
            <div className='post-comments-counter'>
              {feedObject.replies.length}
            </div>
            <div>
              {feedObject.replies.length === 1 ? 'Comment' : 'Comments'}
            </div>
          </>
        ) : (
          'Comments Disabled'
        )}
      </PostCommentsContainer>
    </PostActions>
  );

  return (
    <FeedPostDiv>
      <MainPostContainer ref={feedPostRef}>
        <InteriorDiv>
          <PicAndHeader>
            <UserPicDiv props={feedObject} />
            <PostAuthorAndDate feedObject={feedObject} />
          </PicAndHeader>
          <DropdownDiv>{dropdownOptions()}</DropdownDiv>
        </InteriorDiv>
        <PostText feedObject={feedObject}>
          {feedObject.postText
            ? transformPostText(feedObject.postText, true)
            : ''}
        </PostText>
        {renderMedia()}
        {renderPostActionsSection()}
      </MainPostContainer>
      {renderCommentsSection()}
      <IsTypingComponent feedObject={feedObject} />
      <NewCommentBox feedObject={feedObject} />
    </FeedPostDiv>
  );
};

FeedPost.propTypes = {
  feedObject: PropTypes.instanceOf(Object).isRequired,
};

export default FeedPost;
